import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { auth } from './firebase';
import { CompanySector, COMPANY_DIVISIONS } from '../data';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  authServiceUnavailable?: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bypassAuth: (email: string) => void;
  companyDivisions: CompanySector[];
  fetchCompanyDivisions: () => Promise<CompanySector[]>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// One Firebase Google popup per browser session, even if the login page remounts.
let googlePopupInFlight: Promise<void> | null = null;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authServiceUnavailable, setAuthServiceUnavailable] = useState(false);
  const [companyDivisions, setCompanyDivisions] = useState<CompanySector[]>(COMPANY_DIVISIONS);

  const fetchCompanyDivisions = async () => {
    try {
      // Direct propagation of the company division records upon successful transaction/auth context mapping
      setCompanyDivisions(COMPANY_DIVISIONS);
      return COMPANY_DIVISIONS;
    } catch (err) {
      console.error("Critical: Error retrieving company division registrations:", err);
      setCompanyDivisions(COMPANY_DIVISIONS);
      return COMPANY_DIVISIONS;
    }
  };

  useEffect(() => {
    let completed = false;

    // Failsafe timeout to prevent hanging on sandboxed environments (blocked IndexedDB/cookies)
    const failsafeTimeout = setTimeout(() => {
      if (!completed) {
        console.warn("[AuthContext] Failsafe timeout reached. Marking auth service as unavailable.");
        setAuthServiceUnavailable(true);
        setLoading(false);
      }
    }, 10000);

    const unsubscribe = auth.onAuthStateChanged((user) => {
      completed = true;
      clearTimeout(failsafeTimeout);
      setAuthServiceUnavailable(false);

      // Removed bypass check entirely. Authentication must always rely on real session state or default to guest preview.
      setUser(user);
      if (user) {
        fetchCompanyDivisions();
      } else {
        setCompanyDivisions(COMPANY_DIVISIONS);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(failsafeTimeout);
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = (): Promise<void> => {
    if (googlePopupInFlight) return googlePopupInFlight;
    const provider = new GoogleAuthProvider();
    const attempt = signInWithPopup(auth, provider).then(() => undefined);
    googlePopupInFlight = attempt;
    const clear = () => {
      if (googlePopupInFlight === attempt) googlePopupInFlight = null;
    };
    void attempt.then(clear, clear);
    return attempt;
  };

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    sessionStorage.removeItem("nexorax_bypassed_user_email");
    sessionStorage.removeItem("nexorax_mfa_verified");
    await signOut(auth);
    setUser(null);
  };

  const bypassAuth = (email: string) => {
    // Disabled/Removed all auth bypasses. Security guidelines require explicit real session auth.
    console.warn("Authentication bypass attempted and rejected under strict security policy.");
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      authServiceUnavailable,
      signInWithGoogle, 
      signInWithEmail, 
      signUpWithEmail, 
      logout, 
      bypassAuth,
      companyDivisions,
      fetchCompanyDivisions
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
